import { roleToPermissions } from '@components/share/utils';
import type { DatabaseModel } from '@models/database.model';
import { ProcessModel } from '@models/process.model';
import AsyncStore from '@stores/base/async.store';
import type RootStore from '@stores/root.store';
import {
  type Collaborator,
  type DraftProcess,
  type GenericStudioProcess,
  GenericStudioProcessSchema,
  SmallProcessSchema,
  type StratumnProcess
} from '@stratumn/studio-shared';
import { runInAction } from 'mobx';
import { z } from 'zod';
import { newError } from '@/services/errors/errors';
import type { UserRoles } from '@/types/process.types';
import type { ApiResponseEmpty } from '@/utils/http';
import { parseWithZod } from '@/utils/parseZodSchema';

export type InviteUserDTO = {
  role: UserRoles;
  emails: string[];
};

export type InviteUsersResponseDTO = {
  users: Collaborator[];
};
export type UpdateRoleDTO = {
  role: UserRoles;
  userId: string;
};

export type UpdateProcessDTO = {
  password?: string;
  is_public?: boolean;
  name?: string;
  image?: string;
};

export type AdminUpdateProcessDTO = {
  gitlabRepositoryId?: number;
  image?: string;
  icon?: string;
  organizationName?: string;
  tag?: string | null;
  workspaceActivated?: boolean;
};

export type FetchProcessOptions = {
  password?: string;
};

// TODO: this should be a zod schema declared in shared and also used in the backend
export type CreateProcessDto = {
  name: string;
} & (
  | { draft: false; bpmnWorkflow?: string }
  | { draft: true; template?: string }
);

export type DuplicateProcessOptions = {
  name?: string;
  copyStructure?: boolean;
  copyRepoData?: boolean;
};

export class ProcessStore extends AsyncStore<ProcessModel> {
  private fetchingProcesses: Set<string> = new Set();

  constructor(rootStore: RootStore) {
    super(rootStore, ProcessModel, 'process');
  }

  /** Invalidates the cached process and re-fetches it from the API. */
  public async refetchProcess(id: ProcessModel['id']) {
    this.data.delete(id);
    return this.getorFetchFullProcess(id);
  }

  public async getorFetchFullProcess(
    id: ProcessModel['id'],
    fetchOptions?: FetchProcessOptions
  ): Promise<ProcessModel | undefined> {
    const cachedProcess = this.get(id);
    if (cachedProcess && !cachedProcess.isSmallProcess) {
      return cachedProcess;
    }

    let parsedProcess: GenericStudioProcess | undefined;
    try {
      parsedProcess = await this.fetchAndParseProcess(id, fetchOptions);
    } catch (error) {
      newError('PROCS-gjH3f', error);
      throw error;
    }

    if (!parsedProcess) {
      return;
    }

    const newProcess = this.loadParsedProcess(parsedProcess, fetchOptions);

    if (!newProcess) {
      return;
    }

    this.loadCommonStores(newProcess, parsedProcess);

    if (parsedProcess.draft) {
      this.loadDraftStores(newProcess, parsedProcess);
    } else {
      this.loadStratumnStores(newProcess, parsedProcess);
    }

    return newProcess;
  }

  private loadCommonStores(
    process: ProcessModel,
    parsedProcess: GenericStudioProcess
  ) {
    const databaseStore = this.rootStore.databaseStore;
    const atomStore = this.rootStore.atomStore;

    atomStore.loadAtoms(parsedProcess.atom);

    const loadedDatabases: DatabaseModel[] = [];
    for (const database of parsedProcess.databases) {
      const loadedDatabase = databaseStore.loadDatabaseToStore(database);
      if (loadedDatabase) {
        loadedDatabases.push(loadedDatabase);
      }
    }
    process.setDatabaseIds(loadedDatabases.map(db => db.id));
  }

  private loadDraftStores = (
    process: ProcessModel,
    draftProcess: DraftProcess
  ) => {
    const workflowStore = this.rootStore.workflowStore;
    const workflowIds: string[] = [];

    for (const workflow of draftProcess.workflows) {
      const loadedWf = workflowStore.loadBaseWorkflow(workflow);
      workflowIds.push(loadedWf.id);
    }

    process.setWorkflowIds(workflowIds);
  };

  private loadStratumnStores = (
    process: ProcessModel,
    stratumnProcess: StratumnProcess
  ) => {
    const workflowStore = this.rootStore.workflowStore;
    const transitionStore = this.rootStore.transitionStore;
    const notificationTemplateStore = this.rootStore.notificationTemplateStore;

    /* ------------------------- Handle workflows ------------------------- */
    const workflowIds = workflowStore.loadWorkflows(stratumnProcess.workflows);
    process.setWorkflowIds(workflowIds);

    /* --------------------------- Handle transitions --------------------------- */
    const loadedTransitions = transitionStore.loadTransitions(
      stratumnProcess.Transition
    );
    process.setTransitionIds(
      loadedTransitions.filter(t => t !== undefined).map(t => t.id)
    );

    /* -------------------------- Handle notification templates ------------------*/
    const loadedNotificationTemplates =
      notificationTemplateStore.loadNotificationTemplates(
        stratumnProcess.notificationTemplates
      );
    process.setNotificationTemplateIds(
      loadedNotificationTemplates
        ?.filter(nt => nt !== undefined)
        .map(nt => nt.id) ?? []
    );
  };

  private async fetchAndParseProcess(
    id: ProcessModel['id'],
    fetchOptions?: FetchProcessOptions
  ): Promise<GenericStudioProcess | undefined> {
    if (this.fetchingProcesses.has(id)) {
      return;
    }

    this.fetchingProcesses.add(id);

    const fetchedProcess = await this.httpWrapper
      .get<GenericStudioProcess>(`/${id}`, {
        headers: {
          'x-studio-password': fetchOptions?.password
        }
      })
      .catch(error => {
        newError('PROCS-VYudv', error);
        this.fetchingProcesses.delete(id);
        throw error;
      });

    this.fetchingProcesses.delete(id);

    if (!fetchedProcess) {
      newError('PROCS-cYUWH', `Failed to fetch process ${id}`);
      return;
    }

    const parsedProcess = this.parseFetchedProcess(fetchedProcess);

    return parsedProcess;
  }

  private parseFetchedProcess(
    fetchedProcess: unknown
  ): GenericStudioProcess | undefined {
    const parsedProcess = parseWithZod(
      GenericStudioProcessSchema,
      fetchedProcess,
      'PROCS-veX4f'
    );

    return parsedProcess;
  }

  private loadParsedProcess(
    parsedProcess: GenericStudioProcess,
    fetchOptions?: FetchProcessOptions
  ): ProcessModel | undefined {
    const newProcess = new ProcessModel(
      this,
      parsedProcess.id,
      parsedProcess.name,
      false,
      parsedProcess.icon,
      parsedProcess.published,
      parsedProcess.permission,
      parsedProcess.collaborators,
      parsedProcess.is_public,
      parsedProcess.updatedAt,
      parsedProcess.createdAt,
      parsedProcess.image ?? '',
      parsedProcess.draft,
      parsedProcess.gitlabRepositoryId,
      parsedProcess.organizationName ?? undefined,
      parsedProcess.tag ?? null,
      fetchOptions?.password,
      parsedProcess.deletedAt ? parsedProcess.deletedAt : undefined,
      parsedProcess.workspaceActivated
    );

    this.set(parsedProcess.id, newProcess);

    return newProcess;
  }

  public async updateProcess(
    id: Maybe<ProcessModel['id']>,
    data: UpdateProcessDTO
  ): Promise<ApiResponseEmpty> {
    const response = (await this.httpWrapper
      .patch(`/${id}`, data)
      .catch((error: Error) => {
        newError('PROCS-8Kc5L', error, true, {
          customMessage: 'An error occurred during updating the process'
        });
      })) as ApiResponseEmpty;

    const cachedProcess = this.get(id);

    if (!cachedProcess) {
      newError(
        'PROCS-8Kc5L',
        `Process ${id} not found in cache while updating process`
      );
      return response;
    }

    if (response) {
      runInAction(() => {
        if (data.password) {
          cachedProcess.setPassword(data.password);
        }
        if (data.is_public) {
          cachedProcess.setIsPublic(data.is_public);
        }
        if (data.name) {
          cachedProcess.setName(data.name);
        }
        if (data.image) {
          cachedProcess.setImage(data.image);
        }
      });
    }
    return response;
  }

  public async adminUpdateProcess(
    id: Maybe<ProcessModel['id']>,
    data: AdminUpdateProcessDTO
  ): Promise<ApiResponseEmpty> {
    const response = (await this.httpWrapper
      .patch(`/${id}/admin`, data)
      .catch((error: Error) => {
        newError('PROCS-aDm1n', error, true, {
          customMessage: 'An error occurred during updating the process (admin)'
        });
      })) as ApiResponseEmpty;

    const cachedProcess = this.get(id);

    if (!cachedProcess) {
      newError(
        'PROCS-aDm1n',
        `Process ${id} not found in cache while admin-updating process`
      );
      return response;
    }

    if (response) {
      runInAction(() => {
        if (data.gitlabRepositoryId !== undefined) {
          cachedProcess.setGitlabRepositoryId(data.gitlabRepositoryId);
        }
        if (data.image !== undefined) {
          cachedProcess.setImage(data.image);
        }
        if (data.icon !== undefined) {
          cachedProcess.setIcon(data.icon);
        }
        if (data.organizationName !== undefined) {
          cachedProcess.setOrganizationName(data.organizationName);
        }
        if (data.tag !== undefined) {
          cachedProcess.setTag(data.tag);
        }
        if (data.workspaceActivated !== undefined) {
          cachedProcess.setWorkspaceActivated(data.workspaceActivated);
        }
      });
    }
    return response;
  }

  public async deleteProcess(id: ProcessModel['id']): Promise<boolean> {
    const cachedProcess = this.get(id);

    if (!cachedProcess) {
      newError(
        'PROCS-y2MxQ',
        `Process ${id} not found in cache while deleting process`
      );
      return false;
    }

    await this.httpWrapper.delete(`/${id}`, {
      headers: {
        'x-studio-password': cachedProcess.password
      }
    });

    return this.data.delete(id);
  }

  public async duplicateProcess(
    id: ProcessModel['id'],
    options: DuplicateProcessOptions = {}
  ): Promise<{ id: string; name: string } | undefined> {
    const response = await this.httpWrapper
      .post<{ id: string; name: string }>(`/${id}/duplicate`, options)
      .catch((error: Error) => {
        newError('PROCS-dUpL1', error, true, {
          customMessage: 'An error occurred while duplicating the process'
        });
      });

    return response ?? undefined;
  }

  public async inviteUser(
    processId: string,
    emails: string[],
    role: UserRoles
  ): Promise<Maybe<Collaborator[]>> {
    const dto: InviteUserDTO = {
      emails,
      role
    };

    const cachedProcess = this.get(processId);

    if (!cachedProcess) {
      newError(
        'PROCS-y2MxQ',
        `Process ${processId} not found in cache while inviting users`
      );
      return;
    }

    const response = await this.httpWrapper
      .post<InviteUsersResponseDTO>(`/${processId}/invites`, dto, {
        headers: {
          'x-studio-password': cachedProcess.password
        }
      })
      .catch((error: Error) => {
        newError('PROCS-5m6gS', error, true, {
          customMessage:
            'An error occured, user is not part of Stratumn or already invited'
        });
      });

    if (response) {
      cachedProcess.setCollaborators([
        ...cachedProcess.collaborators,
        ...response.users
      ]);
      return response.users;
    }
    return;
  }

  public async loadRoles(id: string): Promise<Maybe<Collaborator[]>> {
    const cachedProcess = this.get(id);

    if (!cachedProcess) {
      newError(
        'PROCS-QJYgO',
        `Process ${id} not found in cache while loading roles`
      );
      return;
    }

    return await this.httpWrapper.get<Collaborator[]>(`/${id}/roles`, {
      headers: {
        'x-studio-password': cachedProcess.password
      }
    });
  }

  public async updateRole(
    id: ProcessModel['id'],
    role: UserRoles,
    userId: string
  ): Promise<void> {
    const dto: UpdateRoleDTO = {
      role,
      userId
    };
    await this.httpWrapper.patch(`/${id}/roles`, dto);

    const cachedProcess = this.get(id);

    if (!cachedProcess) {
      newError(
        'PROCS-QJYgO',
        `Process ${id} not found in cache while updating role`
      );
      return;
    }

    cachedProcess.updateCollaborator(userId, roleToPermissions(role));
  }

  public async deleteRole(
    id: ProcessModel['id'],
    userId: string
  ): Promise<void> {
    const cachedProcess = this.get(id);

    if (!cachedProcess) {
      newError(
        'PROCS-u6gv_',
        `Process ${id} not found in cache while deleting role`
      );
      return;
    }
    await this.httpWrapper.delete(`/${id}/roles/${userId}`, {
      headers: {
        'x-studio-password': cachedProcess.password
      }
    });

    cachedProcess.setCollaborators(
      cachedProcess.collaborators?.filter(collab => collab.id !== userId)
    );
  }

  public async getAllProcesses() {
    const fetchedProcesses = await this.httpWrapper.get('/');

    const parsedSmallProcesses = parseWithZod(
      z.array(SmallProcessSchema),
      fetchedProcesses,
      'PROC-qok32n'
    );

    if (!parsedSmallProcesses) {
      return;
    }

    for (const process of parsedSmallProcesses) {
      if (this.get(process.id)) {
        continue;
      }

      const newProcess = new ProcessModel(
        this,
        process.id,
        process.name,
        true,
        process.icon,
        process.published,
        process.permission,
        [],
        false,
        process.updatedAt,
        process.createdAt,
        '',
        process.draft,
        process.gitlabRepositoryId,
        undefined,
        process.tag ?? null,
        undefined,
        undefined,
        process.workspaceActivated
      );

      newProcess.setWorkflowIds(process.workflows.map(wf => wf.id));

      this.set(process.id, newProcess);
    }
  }
}
